import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDownIcon, ArrowUpDownIcon, ArrowUpIcon } from 'lucide-react';
import {
  Datatable,
  DatatableBody,
  DatatableCell,
  DatatableHead,
  DatatableHeader,
  DatatableRow,
  Empty,
  EmptyHeader,
  EmptyTitle,
  Skeleton,
  TooltipProvider,
} from '@monksflow/monks-ui';
import {
  CONNECTOR_PLATFORM_LABELS,
  PlatformLogo,
  type ConnectorPlatform,
} from '@/entities/media-connector';
import type { PerformanceBreakdownRow } from '@/entities/media-campaign';
import { formatMetric } from '@/shared/utils/metric-format';
import { CreativeThumbnail } from '../CreativeThumbnail';
import { deriveRates, type DerivedRates } from '../../lib/metrics';

export interface BreakdownTableProps {
  rows: PerformanceBreakdownRow[];
  dimension: 'platform' | 'campaign' | 'creative' | 'placement';
  isLoading?: boolean;
}

type SortKey = 'spend' | 'impressions' | 'clicks' | 'ctr' | 'cpc' | 'cpm' | 'conversions' | 'cpa';
type SortDirection = 'asc' | 'desc';

/**
 * Column heads carry a translation key rather than a resolved label: this table is a module
 * constant, and a `t(...)` here would freeze at import.
 */
const SORTABLE_COLUMNS = [
  { key: 'spend', labelKey: 'performanceDashboard.breakdownTable.spend' },
  { key: 'impressions', labelKey: 'performanceDashboard.breakdownTable.impressions' },
  { key: 'clicks', labelKey: 'performanceDashboard.breakdownTable.clicks' },
  { key: 'ctr', labelKey: 'performanceDashboard.breakdownTable.ctr' },
  { key: 'cpc', labelKey: 'performanceDashboard.breakdownTable.cpc' },
  { key: 'cpm', labelKey: 'performanceDashboard.breakdownTable.cpm' },
  { key: 'conversions', labelKey: 'performanceDashboard.breakdownTable.conv' },
  { key: 'cpa', labelKey: 'performanceDashboard.breakdownTable.cpa' },
] as const satisfies readonly { key: SortKey; labelKey: string }[];

/** The value a row occupies on the given column's scale, or null if it has none. */
function metricValue(
  row: PerformanceBreakdownRow,
  rates: DerivedRates,
  key: SortKey,
): number | null {
  switch (key) {
    case 'spend':
      return row.spendReported ? row.spend : null;
    case 'impressions':
      return row.impressions;
    case 'clicks':
      return row.clicks;
    case 'conversions':
      return row.conversions;
    case 'ctr':
      return rates.ctr;
    case 'cpc':
      return rates.cpc;
    case 'cpm':
      return rates.cpm;
    case 'cpa':
      return rates.cpa;
  }
}

/**
 * A null placement is independent of direction: a row with no value on this scale has
 * no rank on it, so it belongs at the end whether the sort runs ascending or descending.
 * Only the ordering of the real values flips with direction; treating null as 0 would
 * rank a platform that reports nothing above one that genuinely performed poorly.
 */
function compareByKey(direction: SortDirection) {
  return (a: number | null, b: number | null): number => {
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    const diff = a - b;
    return direction === 'asc' ? diff : -diff;
  };
}

/** Only a platform row's `key` is itself a platform slug worth looking up. */
function isPlatformDimension(dimension: BreakdownTableProps['dimension']): boolean {
  return dimension === 'platform';
}

/**
 * A platform row shows the platform's own name; every other row (campaign, creative,
 * placement) shows its label verbatim - a creative/placement row's `key` is
 * platform-prefixed (`meta_ads:cr-hero-15s`), not a platform slug on its own, so the
 * lookup below would never match it even without this guard.
 */
function rowLabel(
  row: PerformanceBreakdownRow,
  dimension: BreakdownTableProps['dimension'],
): string {
  if (!isPlatformDimension(dimension)) return row.label;
  if (Object.hasOwn(CONNECTOR_PLATFORM_LABELS, row.key)) {
    return CONNECTOR_PLATFORM_LABELS[row.key as ConnectorPlatform];
  }
  return row.label;
}

export function BreakdownTable({ rows, dimension, isLoading = false }: BreakdownTableProps) {
  const { t } = useTranslation('projects');
  // Spend descending by default: spend is the number every stakeholder already reads
  // this table to rank by.
  const [sort, setSort] = useState<{ key: SortKey; direction: SortDirection }>({
    key: 'spend',
    direction: 'desc',
  });

  const rowsWithRates = useMemo(
    () =>
      rows.map((row) => ({
        row,
        // Spend is passed as null when the row reports none, so every spend-derived
        // rate falls out of deriveRates as null too, rather than dividing by a zero
        // nobody measured.
        rates: deriveRates({
          spend: row.spendReported ? row.spend : null,
          impressions: row.impressions,
          clicks: row.clicks,
          conversions: row.conversions,
        }),
      })),
    [rows],
  );

  const sortedRows = useMemo(() => {
    const compare = compareByKey(sort.direction);
    return [...rowsWithRates].sort((a, b) =>
      compare(metricValue(a.row, a.rates, sort.key), metricValue(b.row, b.rates, sort.key)),
    );
  }, [rowsWithRates, sort]);

  const handleSort = (key: SortKey) => {
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === 'desc' ? 'asc' : 'desc' }
        : { key, direction: 'desc' },
    );
  };

  if (isLoading) {
    return <Skeleton className="h-40 w-full" data-testid="breakdown-table-skeleton" />;
  }

  if (rows.length === 0) {
    return (
      <Empty data-testid="breakdown-table-empty">
        <EmptyHeader>
          <EmptyTitle>
            {t('performanceDashboard.breakdownTable.nothingDeliveredInThisPeriod')}
          </EmptyTitle>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    // The platform logos carry their name in a tooltip, and this app mounts no provider
    // at its root.
    <TooltipProvider>
      <Datatable data-testid="breakdown-table">
        <DatatableHeader>
          <DatatableRow>
            <DatatableHead scope="col" alignment="left" />
            {SORTABLE_COLUMNS.map(({ key, labelKey }) => {
              const isActive = sort.key === key;
              /* eslint-disable i18next/no-literal-string -- aria-sort token values, not copy */
              const ariaSort = isActive
                ? sort.direction === 'asc'
                  ? 'ascending'
                  : 'descending'
                : 'none';
              /* eslint-enable i18next/no-literal-string */

              return (
                <DatatableHead key={key} scope="col" alignment="right" aria-sort={ariaSort}>
                  <button
                    type="button"
                    className="flex w-full items-center justify-end gap-1 font-medium text-foreground"
                    onClick={() => handleSort(key)}
                  >
                    {t(labelKey)}
                    {isActive ? (
                      sort.direction === 'asc' ? (
                        <ArrowUpIcon className="size-3.5" />
                      ) : (
                        <ArrowDownIcon className="size-3.5" />
                      )
                    ) : (
                      <ArrowUpDownIcon className="size-3.5 text-muted-foreground" />
                    )}
                  </button>
                </DatatableHead>
              );
            })}
          </DatatableRow>
        </DatatableHeader>

        <DatatableBody>
          {sortedRows.map(({ row, rates }) => {
            const label = rowLabel(row, dimension);

            return (
              <DatatableRow key={row.key}>
                <DatatableCell>
                  <span className="flex items-center gap-2">
                    {dimension === 'creative' ? (
                      <CreativeThumbnail previewUrl={row.previewUrl} label={label} />
                    ) : null}
                    {/* A platform row is identified by its logo alone; every other grain
                      keeps its text label, which is the only thing naming that row. */}
                    {isPlatformDimension(dimension) ? (
                      <PlatformLogo platform={row.key} fallbackLabel={label} />
                    ) : (
                      label
                    )}
                  </span>
                </DatatableCell>
                <DatatableCell alignment="right">
                  {formatMetric(row.spendReported ? row.spend : null, 'currency', row.currency)}
                </DatatableCell>
                <DatatableCell alignment="right">
                  {formatMetric(row.impressions, 'integer')}
                </DatatableCell>
                <DatatableCell alignment="right">
                  {formatMetric(row.clicks, 'integer')}
                </DatatableCell>
                <DatatableCell alignment="right">
                  {formatMetric(rates.ctr, 'percent')}
                </DatatableCell>
                <DatatableCell alignment="right">
                  {formatMetric(rates.cpc, 'currency', row.currency)}
                </DatatableCell>
                <DatatableCell alignment="right">
                  {formatMetric(rates.cpm, 'currency', row.currency)}
                </DatatableCell>
                <DatatableCell alignment="right">
                  {formatMetric(row.conversions, 'integer')}
                </DatatableCell>
                <DatatableCell alignment="right">
                  {formatMetric(rates.cpa, 'currency', row.currency)}
                </DatatableCell>
              </DatatableRow>
            );
          })}
        </DatatableBody>
      </Datatable>
    </TooltipProvider>
  );
}
